import { useState } from 'react';
import { ActivityIndicator, Modal, Pressable, Text, View } from 'react-native';
import { abrirCofre, cofreAberto, cofreConfigurado, criarCofre, trocarSenhaCofre } from '../lib/cofre';
import { Btn, Field } from './ui';
import { C } from './theme';
import { t } from '../lib/i18n';

type Modo = 'abrir' | 'criar' | 'trocar';

/** Pede a senha do cofre (ou cria o cofre na primeira vez). pedir() resolve true quando o cofre fica aberto. */
export function useCofreSheet(cor = '#6366F1') {
  const [estado, setEstado] = useState<{ modo: Modo; fim: (ok: boolean) => void } | null>(null);
  const pedir = (modo?: Modo): Promise<boolean> => {
    if (!modo && cofreAberto()) return Promise.resolve(true);
    return new Promise(res => setEstado({ modo: modo ?? (cofreConfigurado() ? 'abrir' : 'criar'), fim: res }));
  };
  const fechar = (ok: boolean) => { estado?.fim(ok); setEstado(null); };
  const tela = <CofreModal modo={estado?.modo ?? null} cor={cor} onFim={fechar} />;
  return { pedir, tela };
}

function CofreModal({ modo, cor, onFim }: { modo: Modo | null; cor: string; onFim: (ok: boolean) => void }) {
  const [s1, setS1] = useState(''); const [s2, setS2] = useState(''); const [s0, setS0] = useState('');
  const [msg, setMsg] = useState(''); const [busy, setBusy] = useState(false);
  const limpar = () => { setS0(''); setS1(''); setS2(''); setMsg(''); };
  const sair = (ok: boolean) => { limpar(); onFim(ok); };
  async function ok() {
    setMsg('');
    try {
      if (modo === 'abrir') {
        setBusy(true);
        if (await abrirCofre(s1)) return sair(true);
        setMsg(t('Senha do cofre incorreta.'));
      } else {
        if (s1.length < 6) return setMsg(t('A senha do cofre precisa ter 6 caracteres ou mais.'));
        if (s1 !== s2) return setMsg(t('As senhas não conferem.'));
        setBusy(true);
        if (modo === 'criar') { await criarCofre(s1); return sair(true); }
        if (await trocarSenhaCofre(s0, s1)) return sair(true);
        setMsg(t('Senha do cofre incorreta.'));
      }
    } catch (e: any) { setMsg(String(e?.message ?? e)); }
    finally { setBusy(false); }
  }
  return (
    <Modal visible={!!modo} transparent animationType="fade" onRequestClose={() => sair(false)}>
      <Pressable onPress={() => sair(false)} style={{ flex: 1, backgroundColor: C.overlay, justifyContent: 'center', padding: 20 }}>
        <Pressable style={{ backgroundColor: C.card, borderRadius: 20, padding: 20, gap: 12, maxWidth: 480, width: '100%', alignSelf: 'center' }}>
          <Text style={{ fontSize: 36, textAlign: 'center' }}>🔐</Text>
          <Text style={{ color: C.text, fontWeight: '800', fontSize: 18, textAlign: 'center' }}>
            {modo === 'criar' ? t('Criar o cofre') : modo === 'trocar' ? t('Trocar a senha do cofre') : t('Abrir o cofre')}
          </Text>
          <Text style={{ color: C.sub, fontSize: 13, textAlign: 'center' }}>
            {modo === 'criar'
              ? t('Os dados do cofre são criptografados aqui no dispositivo (AES-256) antes de ir para a nuvem. Nem o suporte consegue ler. Se esquecer a senha do cofre, esses dados não podem ser recuperados.')
              : t('Digite a senha do cofre. Ele fecha sozinho depois de 5 minutos sem uso.')}
          </Text>
          {modo === 'trocar' && <Field value={s0} onChangeText={setS0} placeholder={t('Senha atual do cofre')} secureTextEntry autoCapitalize="none" />}
          <Field value={s1} onChangeText={setS1} placeholder={modo === 'abrir' ? t('Senha do cofre') : t('Senha nova do cofre (6 caracteres ou mais)')} secureTextEntry autoCapitalize="none"
            onSubmitEditing={modo === 'abrir' ? ok : undefined} autoFocus />
          {modo !== 'abrir' && <Field value={s2} onChangeText={setS2} placeholder={t('Repita a senha do cofre')} secureTextEntry autoCapitalize="none" onSubmitEditing={ok} />}
          {!!msg && <Text style={{ color: C.danger }}>{msg}</Text>}
          {busy ? <View style={{ padding: 14, alignItems: 'center', gap: 6 }}><ActivityIndicator color={cor} /><Text style={{ color: C.sub, fontSize: 12 }}>{t('Protegendo a chave...')}</Text></View>
            : <Btn cor={cor} label={modo === 'abrir' ? t('Abrir') : t('Salvar')} onPress={ok} />}
          <Pressable onPress={() => sair(false)} style={{ alignItems: 'center', padding: 6 }}><Text style={{ color: C.sub }}>{t('Cancelar')}</Text></Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
